import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { distinctUntilChanged } from 'rxjs';
import { IGlobalSettings, IGtagEventConfig } from '../domain/settings.model';
import { getGlobalSettings } from '../state/global';

declare const gtag: Function;

/**
 * Fires Google Analytics 4 custom events for action buttons (apply, submit,
 * save-draft, etc.). Tenant opts in by:
 *   1. Setting `google_analytics_code` (e.g., G-XXXXXXX) so `app.component.ts`
 *      auto-injects gtag.js
 *   2. Creating a `google_analytics_events` spa_settings row whose JSON value
 *      maps semantic event keys to GA event configs
 *
 * For tenants without either of those, every `trackEvent()` call is a no-op —
 * the gtag script is never loaded, no GA traffic, no console noise. Multi-tenant
 * isolation is enforced by the brand-details API (each tenant gets only its own
 * config).
 */
@Injectable({ providedIn: 'root' })
export class GtagService {
  private eventMap: Record<string, IGtagEventConfig> | null = null;
  private gtagConfigured = false;

  constructor(private store: Store) {
    // Brand details settle once per session; subscribe and cache the event map.
    this.store.pipe(select(getGlobalSettings), distinctUntilChanged()).subscribe((settings: IGlobalSettings) => {
      this.eventMap = settings?.site?.google_analytics_events || null;
      this.gtagConfigured = !!settings?.site?.google_analytics;
    });
  }

  /**
   * Fire a GA4 custom event. Looks up the semantic key in the tenant's
   * google_analytics_events config. Safe no-op when:
   *  - tenant has no google_analytics_code (gtag not loaded)
   *  - tenant has no event map
   *  - the requested eventKey isn't mapped
   *  - gtag global isn't available yet (script hasn't loaded)
   *
   * Tracking must never disrupt UX, so all errors are swallowed.
   */
  trackEvent(eventKey: string, onComplete?: () => void): void {
    /* onComplete (optional) is invoked once the GA hit has been sent — use it
       to gate a navigation/redirect that follows the event, so the hit isn't
       cancelled mid-flight (this was why popup_form_submit, which fires right
       before navigating to /auth/register, never reached GA even with beacon).
       It is ALSO called on every no-op path (tenant without GA, unmapped key,
       gtag not loaded) and via a timeout safety net, so the caller's flow
       never stalls waiting on analytics. */
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      try { onComplete?.(); } catch { /* never let the callback break UX */ }
    };

    if (!this.gtagConfigured) { done(); return; }
    const config = this.eventMap?.[eventKey];
    if (!config?.action) { done(); return; }
    if (typeof gtag !== 'function') { done(); return; }

    try {
      /* transport_type:'beacon' makes GA4 send the hit via
         navigator.sendBeacon, which survives a page unload / navigation.
         event_callback fires once GA has dispatched the hit — we navigate
         from there so the redirect can't cancel an in-flight request.
         event_timeout bounds the wait so a blocked/slow GA never strands the
         user; the setTimeout below is a second belt-and-suspenders guard. */
      const params: Record<string, any> = { ...(config.params || {}), transport_type: 'beacon' };
      if (onComplete) {
        params['event_callback'] = done;
        params['event_timeout'] = 1000;
      }
      gtag('event', config.action, params);
    } catch {
      /* swallow — analytics must never disrupt user flow */
      done();
      return;
    }

    /* Safety net: if GA never invokes event_callback (ad blocker, network
       drop), still let the caller proceed shortly after. */
    if (onComplete) {
      setTimeout(done, 1200);
    }
  }
}
